import {
    ActionReducerMapBuilder,
    AsyncThunk,
    CaseReducer,
    createAsyncThunk,
    createSlice,
    Draft, PayloadAction
} from '@reduxjs/toolkit'
import { ApiState } from "@baseTypes/baseType";
import { weatherIcons } from "../../../utils/mapWeatherIconByName";
import WeatherService from "../../../api/dashboards/WeatherService";

export interface WeatherData {
    date: number,
    temp: number,
    type: keyof typeof weatherIcons
}

export interface WeatherState extends ApiState {
    data: WeatherData[]
}

const initialState: WeatherState = {
    data: [],
    apiError: undefined,
    isApiLoading: false
}

const fetchWeather = createAsyncThunk(
    'weather/by_work',
    async ({ geoZoneIds, from, to }: { geoZoneIds: number[], from: number, to: number }, thunkAPI) => {
        try {
            const response = await WeatherService.fetchWeatherByGeozoneIds(geoZoneIds, from, to, thunkAPI)
            if (response && response.data) {
                return response.data
            } else {
                throw new Error('Invalid response ' + response)
            }
        } catch (e: any) {
            const message = `An error occurred while fetching API ` + (e?.message || e || '')
            return thunkAPI.rejectWithValue({ message })
        }
    }
)

type FulfillMeta<P> = {
    arg: P;
    requestId: string;
    requestStatus: "fulfilled";
}
type RejectedMeta<P> = {
    arg: P;
    requestId: string;
    requestStatus: 'rejected';
    aborted: boolean;
    condition: boolean;
}

export interface ApplyExteraReducerConfig<S, R,
    T extends string,
    M> {
    // TODO!
    // prop?: keyof S,
    rejectReducer?: CaseReducer<S, PayloadAction<unknown, T, RejectedMeta<M>>>
    fulfillReducer?: CaseReducer<S, PayloadAction<R, T, FulfillMeta<M>>>
}

export const applyExtraReducer = function <S extends ApiState, R, P>(
    builder: ActionReducerMapBuilder<S>,
    thunk: AsyncThunk<R, P, {}>,
    config: ApplyExteraReducerConfig<S, R, string, P> = {}
) {
    builder.addCase(thunk.rejected, (state, action) => {
        state.isApiLoading = false
        // @ts-ignore
        state.apiError = action.payload?.message;
        if (config.rejectReducer) config.rejectReducer(state, action)
    })
    builder.addCase(thunk.pending, (state) => {
        state.isApiLoading = true
        state.apiError = undefined
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        state.isApiLoading = false
        state.apiError = undefined
        if (config.fulfillReducer) {
            return config.fulfillReducer(state, action)
        } else {
            // @ts-ignore
            state.data = action.payload
        }
    })
}

const weatherSlice = createSlice({
    name: 'weatherSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        applyExtraReducer(builder, fetchWeather)
    }
})

export const actions = { ...weatherSlice.actions, fetchWeather }
export default weatherSlice.reducer